import { getCscaSubjectName } from '../utils/cscaTestPresentation';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CheckCircle, ChevronLeft, ChevronRight, Clock, Flag, Grid, History, Send } from 'lucide-react';
import MathText from '../components/MathText';
import CscaExplanationLanguageSwitch, { useCscaExplanationLanguage } from '../components/CscaExplanationLanguage';
import { useAuthStore, useTestStore } from '../store';
import { api } from '../utils/api';
import { localApi } from '../utils/local-api';

const formatDuration = (seconds: number) => {
  const safe = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const secs = safe % 60;
  return hours > 0
    ? `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
    : `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const CSCAExam: React.FC = () => {
  const { paperId = '' } = useParams<{ paperId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const {
    currentTest,
    startTest,
    answerQuestion,
    goToQuestion,
    clearCurrentTest,
  } = useTestStore();
  const subjectLevel = paperId.includes('_IPA_') ? 2 : paperId.includes('_IPS_') ? 1 : Number(searchParams.get('level') || 3);
  const subjectName = getCscaSubjectName(subjectLevel);
  const subjectPart = subjectLevel === 2 ? 'chinese-stem' : subjectLevel === 1 ? 'chinese-humanities' : 'math';
  const topicCoarse = String(searchParams.get('coarse') || '').trim();
  const topicFine = String(searchParams.get('fine') || '').trim();
  const requestedCount = Math.max(1, Math.floor(Number(searchParams.get('count') || 10)));
  const isRandomPaper = location.pathname === '/csca/random/exam';
  const isTopicPractice = location.pathname === '/csca/topic-practice';
  const isMoocTopicPractice = location.pathname === '/csca/mooc/topic-practice';
  const isMoocRandom = location.pathname === '/csca/mooc/random/exam';
  const isMooc = isMoocTopicPractice || isMoocRandom;
  const isPracticeMode = isTopicPractice || isMooc;
  const isIpaSplitPaper = paperId.toUpperCase() === 'CSCA_IPA_26_04' || paperId.toUpperCase() === 'CSCA_IPA_26_06';
  const backPath = isRandomPaper ? '/csca/random' : isTopicPractice ? `/csca/simulation/${subjectLevel}/topics` : isMoocTopicPractice ? '/csca/mooc/topics' : isMoocRandom ? '/csca/mooc/random' : `/csca/simulation/${subjectLevel}/papers`;
  const scope = isRandomPaper
    ? 'csca-random:math'
    : isMoocRandom
    ? 'csca-mooc-random:math'
    : isMoocTopicPractice
    ? `csca-mooc-topic:${topicCoarse}:${topicFine || '*'}`
    : isTopicPractice
    ? `csca-topic:${subjectLevel}:${topicCoarse}:${topicFine || '*'}`
    : `csca:${paperId.toUpperCase()}`;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [examTitle, setExamTitle] = useState('CSCA 数学');
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showAnswerCard, setShowAnswerCard] = useState(false);
  const [marked, setMarked] = useState<number[]>([]);
  const [saveStatus, setSaveStatus] = useState('');
  const [result, setResult] = useState<any | null>(null);
  const { language: explanationLanguage, changeLanguage: changeExplanationLanguage } = useCscaExplanationLanguage();
  const timerAnchor = useRef<{ remaining: number; at: number } | null>(null);
  const questionTiming = useRef<Record<number, number>>({});
  const lastQuestionVisit = useRef<{ index: number; at: number } | null>(null);

  useEffect(() => {
    if (!token || ((isTopicPractice || isMoocTopicPractice) ? !topicCoarse : !isRandomPaper && !isMoocRandom && !paperId)) {
      navigate(backPath, { replace: true });
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    const startRequest = isRandomPaper
      ? api.startCscaRandomPaper(token)
      : isMoocRandom
      ? api.startCscaMoocRandom(token)
      : isMoocTopicPractice
      ? api.startCscaMoocTopicPractice(topicCoarse, topicFine || null, requestedCount, token)
      : isTopicPractice
      ? api.startCscaTopicPractice(topicCoarse, topicFine || null, requestedCount, token, subjectLevel)
      : api.startCscaPaper(paperId, token);
    startRequest
      .then(response => {
        if (cancelled) return;
        const questions = response.questions.map(question => ({
          id: question.question_id,
          level: question.level || subjectLevel,
          part: question.part || subjectPart,
          question: question.question_text,
          options: question.options,
          correctAnswer: -1,
          gradingPending: question.grading_pending,
          explanation: question.explanation,
          media: question.media || undefined,
          order: question.order,
          bankVersion: 'CSCA',
          questionNumber: question.question_number ?? null,
          passageId: question.passage_id,
          passageText: question.passage_text,
          questionPrompt: question.question_prompt,
        }));
        const initialAnswers = new Array<number | null>(questions.length).fill(null);
        let initialIndex = 0;
        if (response.resumed && response.progress) {
          initialIndex = Math.max(0, Math.min(Number(response.progress.index || 0), questions.length - 1));
          Object.entries(response.progress.answers || {}).forEach(([index, answer]) => {
            const numericIndex = Number(index);
            if (numericIndex >= 0 && numericIndex < questions.length && typeof answer === 'number') initialAnswers[numericIndex] = answer;
          });
        }
        // Switching between mobile and desktop layouts remounts this page. Keep
        // answers still waiting for the debounced progress save in this browser.
        const localTest = useTestStore.getState().currentTest;
        if (response.resumed && localTest?.testId === response.test_session_id && localTest.examScope === scope
          && localTest.questions.length === questions.length
          && localTest.questions.every((question, index) => question.id === questions[index].id)) {
          localTest.answers.forEach((answer, index) => { if (typeof answer === 'number') initialAnswers[index] = answer; });
          initialIndex = Math.max(0, Math.min(localTest.currentQuestionIndex, questions.length - 1));
        }
        startTest(subjectLevel, subjectPart, questions, response.test_session_id, initialIndex, initialAnswers, scope);
        setExamTitle(response.exam_title || (isRandomPaper
          ? 'CSCA 数学 · 随机组卷'
          : isMoocRandom
          ? 'CSCA 数学 · 随机 MOOC'
          : isMoocTopicPractice
          ? `CSCA 数学 · MOOC · ${topicFine ? `${topicCoarse} / ${topicFine}` : topicCoarse} · 分类练习`
          : isTopicPractice
          ? `CSCA ${subjectName} · ${topicFine ? `${topicCoarse} / ${topicFine}` : topicCoarse} · 题型练习`
          : `CSCA ${subjectName} ${response.paper_year || ''}年${response.paper_month || ''}月`));
        const duration = Number(response.duration_seconds || (isPracticeMode ? response.questions.length * 75 : 3600));
        const startedAt = Date.parse(response.started_at || new Date().toISOString());
        const serverNow = Date.parse(response.server_now || new Date().toISOString());
        const elapsed = Math.max(0, Math.floor((serverNow - startedAt) / 1000));
        const remaining = Math.max(0, duration - elapsed);
        setDurationSeconds(duration);
        setRemainingSeconds(remaining);
        timerAnchor.current = { remaining, at: Date.now() };
        try {
          const raw = localStorage.getItem(`csca-marks:${response.test_session_id}`);
          setMarked(raw ? JSON.parse(raw) : []);
        } catch {
          setMarked([]);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || (isRandomPaper ? '无法生成 CSCA 随机试卷' : isMoocRandom ? '无法生成随机 MOOC' : isMoocTopicPractice ? '无法加载 MOOC 分类练习' : isTopicPractice ? '无法加载 CSCA 题型练习' : '无法加载 CSCA 试卷'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [backPath, isMoocRandom, isMoocTopicPractice, isPracticeMode, isRandomPaper, isTopicPractice, navigate, paperId, requestedCount, scope, startTest, token, topicCoarse, topicFine, subjectLevel, subjectName, subjectPart]);

  const scopedTest = currentTest?.examScope === scope ? currentTest : null;

  useEffect(() => {
    if (!timerAnchor.current || result) return;
    const interval = window.setInterval(() => {
      const anchor = timerAnchor.current;
      if (!anchor) return;
      setRemainingSeconds(Math.max(0, anchor.remaining - Math.floor((Date.now() - anchor.at) / 1000)));
    }, 500);
    return () => window.clearInterval(interval);
  }, [durationSeconds, result]);

  useEffect(() => {
    if (!scopedTest || result) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [result, scopedTest]);

  useEffect(() => {
    if (!scopedTest?.testId || !token || result) return;
    const timeout = window.setTimeout(() => {
      const answers = Object.fromEntries(scopedTest.answers.flatMap((answer, index) => answer == null ? [] : [[String(index), answer]]));
      localApi.saveProgress(scopedTest.testId!, scopedTest.currentQuestionIndex, answers, token)
        .then(() => setSaveStatus(`已保存 ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`))
        .catch(() => setSaveStatus('进度保存失败，请检查网络'));
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [result, scopedTest?.answers, scopedTest?.currentQuestionIndex, scopedTest?.testId, token]);

  useEffect(() => {
    if (!scopedTest?.testId) return;
    localStorage.setItem(`csca-marks:${scopedTest.testId}`, JSON.stringify(marked));
  }, [marked, scopedTest?.testId]);

  useEffect(() => {
    if (!scopedTest) return;
    const now = Date.now();
    const previous = lastQuestionVisit.current;
    if (previous) questionTiming.current[previous.index] = (questionTiming.current[previous.index] || 0) + Math.max(0, Math.floor((now - previous.at) / 1000));
    lastQuestionVisit.current = { index: scopedTest.currentQuestionIndex, at: now };
  }, [scopedTest?.currentQuestionIndex, scopedTest?.testId]);

  const unansweredCount = useMemo(() => scopedTest?.answers.filter(answer => answer == null).length || 0, [scopedTest?.answers]);

  const submitExam = useCallback(async (timedOut = false) => {
    if (!scopedTest?.testId || !token || isSubmitting) return;
    setIsSubmitting(true);
    setError('');
    setShowSubmitDialog(false);
    const now = Date.now();
    const activeVisit = lastQuestionVisit.current;
    if (activeVisit) {
      questionTiming.current[activeVisit.index] = (questionTiming.current[activeVisit.index] || 0) + Math.max(0, Math.floor((now - activeVisit.at) / 1000));
      lastQuestionVisit.current = { index: activeVisit.index, at: now };
    }
    const elapsedSeconds = Math.max(0, Math.min(durationSeconds, durationSeconds - remainingSeconds));
    const answers = scopedTest.questions.map((question, index) => ({
      question_id: question.id,
      chosen_option_index: scopedTest.answers[index],
      chose_buzhidao: false,
      time_spent_seconds: questionTiming.current[index] || 0,
    }));
    try {
      await api.submitTest(scopedTest.testId, answers, token, { elapsedSeconds });
      const detail = await api.getTestDetail(scopedTest.testId, token);
      setResult({ ...detail, timedOut });
      localStorage.removeItem(`csca-marks:${scopedTest.testId}`);
      clearCurrentTest();
    } catch (err: any) {
      setError(err?.message || '提交失败，请检查网络后重试');
    } finally {
      setIsSubmitting(false);
    }
  }, [clearCurrentTest, durationSeconds, isSubmitting, remainingSeconds, scopedTest, token]);

  useEffect(() => {
    if (remainingSeconds === 0 && durationSeconds > 0 && scopedTest && !result && !isSubmitting) void submitExam(true);
  }, [durationSeconds, isSubmitting, remainingSeconds, result, scopedTest, submitExam]);

  const handleBack = () => {
    if (!scopedTest || window.confirm('离开考试页面前会保留已保存进度。确定离开吗？')) navigate(backPath);
  };

  if (loading) return <div className="py-20 text-center font-semibold text-[#312E81]">{isRandomPaper ? '正在按往年平均题量生成48题随机试卷…' : isMoocRandom ? '正在从 MOOC 题库随机抽题…' : isMoocTopicPractice ? '正在按所选分类加载 MOOC 题目…' : isTopicPractice ? '正在按所选题型从 D1 加载题目…' : '正在按原试卷顺序加载题目…'}</div>;
  if (error && !scopedTest && !result) return <div className="mx-auto max-w-2xl rounded-2xl border border-red-200 bg-red-50 p-5 text-red-700">{error}</div>;

  if (result) {
    const items = Array.isArray(result.items) ? result.items : [];
    const gradedItems = items.filter((item: any) => !item.grading_pending);
    const pendingCount = items.length - gradedItems.length;
    const correct = gradedItems.filter((item: any) => item.correct).length;
    const unanswered = gradedItems.filter((item: any) => item.chosen_option_index == null && !item.chose_buzhidao).length;
    const wrong = gradedItems.length - correct - unanswered;
    const score = Number(result.weighted_score || 0);
    return (
      <div className="mx-auto max-w-5xl space-y-6">
        <section className="rounded-3xl bg-white p-6 shadow-sm md:p-8">
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="text-sm font-bold text-[#4F46E5]">{examTitle}</div>
              <h1 className="mt-1 text-3xl font-extrabold text-[#312E81]">{isPracticeMode ? '练习成绩' : '考试成绩'}</h1>
              {result.timedOut && <div className="mt-2 text-sm font-semibold text-amber-700">考试时间结束，系统已自动交卷。</div>}
            </div>
            <div className="text-center md:text-right">
              <div className="text-5xl font-black text-[#4F46E5]">{gradedItems.length ? score.toFixed(1) : '—'}</div>
              <div className="text-sm text-gray-400">{gradedItems.length ? '满分 100' : '暂无可计分题目'}</div>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-6">
            {[
              ['总题数', items.length], ['待核对（不计分）', pendingCount], ['答对', correct], ['答错', wrong], ['未答', unanswered],
              ['用时', formatDuration(Number(result.elapsed_seconds || durationSeconds - remainingSeconds))],
              ['原始得分', `${correct}/${gradedItems.length}`],
            ].map(([label, value]) => (
              <div key={String(label)} className="rounded-2xl bg-indigo-50 p-3 text-center">
                <div className="text-xs font-semibold text-gray-500">{label}</div>
                <div className="mt-1 text-lg font-extrabold text-[#312E81]">{value}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/history" className="inline-flex items-center gap-2 rounded-xl bg-[#4F46E5] px-5 py-3 font-bold text-white hover:bg-indigo-700"><History className="h-4 w-4" /> 查看考试记录</Link>
            <Link to={backPath} className="rounded-xl border border-indigo-200 px-5 py-3 font-bold text-[#4F46E5] hover:bg-indigo-50">{isRandomPaper ? '返回随机组卷' : isMoocRandom ? '返回随机 MOOC' : isMoocTopicPractice ? '返回 MOOC 分类' : isTopicPractice ? '返回题型统计' : '返回试卷列表'}</Link>
          </div>
          {items.length > 0 && (
            <div className="mt-6 flex items-center gap-3 border-t border-indigo-100 pt-5">

              <CscaExplanationLanguageSwitch language={explanationLanguage} onChange={changeExplanationLanguage} />
            </div>
          )}
        </section>

        <section className="space-y-4">
          {items.map((item: any, index: number) => {
            const selected = item.chosen_option_index;
            const originalNumber = Number(String(item.question_id || '').slice(-3));
            const positionLabel = String(originalNumber).padStart(2, '0');
            return (
              <article key={item.question_id} className={`rounded-3xl border-2 bg-white p-5 shadow-sm md:p-7 ${item.grading_pending ? 'border-slate-200' : item.correct ? 'border-green-200' : selected == null ? 'border-amber-200' : 'border-red-200'}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-bold text-gray-500">第 {index + 1} 道 · {isRandomPaper ? `出题位置 ${positionLabel}` : isMooc ? `${String(item.question_id).startsWith('CSCA_SIM_3_') ? '模拟题' : 'AI 题'} ${String(originalNumber).padStart(3, '0')}` : isIpaSplitPaper ? `试卷题号 ${index + 1} · 来源题号 ${originalNumber}` : `原题号 ${originalNumber}`} <span className="ml-2 text-xs font-normal">{item.question_id}</span></div>
                  <div className={`rounded-full px-3 py-1 text-xs font-bold ${item.grading_pending ? 'bg-slate-100 text-slate-600' : item.correct ? 'bg-green-50 text-green-700' : selected == null ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>{item.grading_pending ? '待核对 · 不计分' : item.correct ? '正确' : selected == null ? '未答' : '错误'}</div>
                </div>
                <MathText content={item.question_text} className="mt-5 block text-lg font-semibold leading-8 text-[#312E81]" />
                <div className="mt-5 space-y-2.5">
                  {(item.options || []).map((option: string, optionIndex: number) => {
                    const isCorrectOption = (item.correct_option_indices || [item.correct_option_index]).includes(optionIndex);
                    const isSelected = optionIndex === selected;
                    const selectedWrong = isSelected && !item.grading_pending;
                    return (
                      <div key={optionIndex} className={`flex items-start gap-3 rounded-2xl border-2 p-3.5 ${isCorrectOption ? 'border-green-500 bg-green-50' : selectedWrong ? 'border-red-500 bg-red-50' : 'border-gray-100'}`}>
                        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg font-bold ${isCorrectOption ? 'bg-green-600 text-white' : selectedWrong ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-500'}`}>{String.fromCharCode(65 + optionIndex)}</span>
                        <MathText content={option} className="min-w-0 flex-1 leading-7 text-gray-800" />
                      </div>
                    );
                  })}
                </div>
                <div className="mt-5 grid gap-3 md:grid-cols-2">
                  <div className="rounded-2xl bg-gray-50 p-4 text-sm"><div className="mb-1 font-bold text-gray-500">你的答案</div>{selected == null ? '未作答' : String.fromCharCode(65 + selected)}</div>
                  <div className="rounded-2xl bg-green-50 p-4 text-sm"><div className="mb-1 font-bold text-green-700">正确答案</div>{item.grading_pending ? '待核对' : (item.correct_option_indices || [item.correct_option_index]).map((answer: number) => String.fromCharCode(65 + answer)).join(' / ')}</div>
                </div>
                <div className="mt-4 rounded-2xl border border-indigo-100 bg-indigo-50 p-4">
                  <div className="mb-2 font-bold text-[#312E81]">解析</div>
                  <MathText content={explanationLanguage === 'ID' && item.explanation_id ? item.explanation_id : item.explanation || '暂无解析'} className="leading-7 text-indigo-950" />
                  {explanationLanguage === 'ID' && !item.explanation_id && <div className="mt-2 text-xs text-indigo-600">Terjemahan ID belum tersedia; pembahasan CN ditampilkan.</div>}
                </div>
              </article>
            );
          })}
        </section>
      </div>
    );
  }

  if (!scopedTest || scopedTest.questions.length === 0) return <div className="py-20 text-center text-gray-500">没有可用题目</div>;
  const currentIndex = scopedTest.currentQuestionIndex;
  const question = scopedTest.questions[currentIndex];
  const questionPosition = question.questionNumber ?? Number(question.id.slice(-3));
  const questionPositionLabel = String(questionPosition).padStart(2, '0');
  const isMarked = marked.includes(currentIndex);
  const progress = ((currentIndex + 1) / scopedTest.questions.length) * 100;
  const groupQuestions = scopedTest.questions.map((item, index) => ({item, index}))
    .filter(({item, index}) => question.passageId ? item.passageId === question.passageId : index === currentIndex);
  const groupStart = groupQuestions[0].index;
  const groupEnd = groupQuestions[groupQuestions.length - 1].index;
  const isPassageGroup = !!question.passageId;
  const navigateGroup = (index: number) => {
    goToQuestion(index);
    requestAnimationFrame(() => document.getElementById('csca-question-group')?.scrollIntoView({block:'start'}));
  };
  const goPreviousGroup = () => navigateGroup(Math.max(0, groupStart - 1));
  const goNextGroup = () => groupEnd === scopedTest.questions.length - 1 ? setShowSubmitDialog(true) : navigateGroup(groupEnd + 1);

  const answerCard = (
    <div className="space-y-4">
      <div className="flex items-center justify-between"><h2 className="font-bold text-[#312E81]">答题卡</h2><span className="text-xs text-gray-400">已答 {scopedTest.questions.length - unansweredCount}/{scopedTest.questions.length}</span></div>
      <div className="grid grid-cols-6 gap-2 md:grid-cols-5">
        {scopedTest.questions.map((_item, index) => {
          const answered = scopedTest.answers[index] != null;
          const flagged = marked.includes(index);
          return (
            <button key={index} type="button" onClick={() => { goToQuestion(index); setShowAnswerCard(false); requestAnimationFrame(() => document.getElementById(`csca-question-${scopedTest.questions[index].id}`)?.scrollIntoView({block:'start'})); }} className={`relative h-10 rounded-xl border text-sm font-bold ${index === currentIndex ? 'border-[#4F46E5] bg-[#4F46E5] text-white' : answered ? 'border-green-200 bg-green-50 text-green-700' : 'border-gray-200 bg-white text-gray-500'}`}>
              {index + 1}{flagged && <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-amber-500" />}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-gray-500"><span><i className="mr-1 inline-block h-2.5 w-2.5 rounded-full bg-green-400" />已答</span><span><i className="mr-1 inline-block h-2.5 w-2.5 rounded-full bg-white ring-1 ring-gray-300" />未答</span><span><i className="mr-1 inline-block h-2.5 w-2.5 rounded-full bg-amber-500" />标记</span></div>
      <button type="button" onClick={() => setShowSubmitDialog(true)} className="flex w-full items-center justify-center gap-2 rounded-xl bg-green-600 px-4 py-3 font-bold text-white hover:bg-green-700"><Send className="h-4 w-4" /> {isPracticeMode ? '提交练习' : '提交试卷'}</button>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl">
      {error && <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}
      <header className="sticky top-16 z-20 mb-5 rounded-2xl border border-indigo-100 bg-white/95 p-4 shadow-sm backdrop-blur md:top-24">
        <div className="flex items-center justify-between gap-3">
          <button type="button" onClick={handleBack} className="flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-[#4F46E5]"><ArrowLeft className="h-4 w-4" /><span className="hidden sm:inline">返回列表</span></button>
          <div className="min-w-0 text-center"><div className="truncate font-bold text-[#312E81]">{examTitle}</div><div className="text-xs text-gray-400">第 {currentIndex + 1} / {scopedTest.questions.length} 道</div></div>
          <div className={`flex items-center gap-2 rounded-xl px-3 py-2 font-mono font-bold ${remainingSeconds <= 300 ? 'bg-red-50 text-red-600' : 'bg-indigo-50 text-[#4F46E5]'}`}><Clock className="h-4 w-4" /> {formatDuration(remainingSeconds)}</div>
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100"><div className="h-full rounded-full bg-[#4F46E5] transition-all" style={{ width: `${progress}%` }} /></div>
      </header>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_300px]">
        <main id="csca-question-group" className="scroll-mt-48 rounded-3xl border border-indigo-50 bg-white p-5 shadow-sm md:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm font-bold text-gray-500">{isPassageGroup ? `本组第 ${groupStart + 1}–${groupEnd + 1} 道 · ${groupQuestions.length} 道小题` : `当前第 ${currentIndex + 1} 道 · ${isRandomPaper ? `出题位置 ${questionPositionLabel}` : isMooc ? `${question.id.startsWith('CSCA_SIM_3_') ? '模拟题' : 'AI 题'} ${String(questionPosition).padStart(3, '0')}` : isIpaSplitPaper ? `试卷题号 ${questionPosition}` : `原题号 ${questionPosition}`}`}</div>
            <button type="button" onClick={() => setMarked(previous => isMarked ? previous.filter(index => index !== currentIndex) : [...previous, currentIndex])} className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-bold ${isMarked ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}><Flag className={`h-4 w-4 ${isMarked ? 'fill-current' : ''}`} /> {isMarked ? '已标记' : '标记题目'}</button>
          </div>
          {isPassageGroup && <section data-testid="csca-shared-passage" className="mt-6 scroll-mt-48 rounded-2xl bg-indigo-50/60 p-4 md:p-6">
            <h2 className="mb-3 font-bold text-[#312E81]">共用材料 · 本组 {groupQuestions.length} 道小题</h2>
            <MathText content={question.passageText || ''} className="block text-lg leading-9 text-slate-800" />
          </section>}
          {groupQuestions.map(({item, index: itemIndex}) => <section id={`csca-question-${item.id}`} key={item.id} data-question-id={item.id} className="mt-6 scroll-mt-48 border-t border-indigo-50 pt-5 first:border-0">
            {isPassageGroup && <h3 className="mb-3 font-bold text-indigo-700">{isIpaSplitPaper ? '试卷题号' : '原题号'} {item.questionNumber ?? Number(item.id.slice(-3))}</h3>}
            {item.gradingPending && <p className="mb-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-800">原题待核对，本题暂不计分。</p>}
            <MathText content={isPassageGroup ? item.questionPrompt || item.question : item.question} className="block text-xl font-semibold leading-9 text-[#312E81]" />
            <div className="mt-5 space-y-3">
              {item.options.map((option, optionIndex) => {
                const selected = scopedTest.answers[itemIndex] === optionIndex;
                return <button key={optionIndex} type="button" onClick={() => { goToQuestion(itemIndex); answerQuestion(optionIndex); }} aria-pressed={selected} className={`flex w-full items-start gap-3 rounded-2xl border-2 p-4 text-left transition-all ${selected ? 'border-[#4F46E5] bg-indigo-50 shadow-sm' : 'border-gray-100 bg-white hover:border-indigo-200 hover:bg-indigo-50/40'}`}>
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl font-bold ${selected ? 'bg-[#4F46E5] text-white' : 'bg-gray-100 text-gray-500'}`}>{String.fromCharCode(65 + optionIndex)}</span>
                  <MathText content={option} className="min-w-0 flex-1 pt-1 text-base leading-7 text-gray-800" />
                  {selected && <CheckCircle className="mt-1 h-5 w-5 shrink-0 text-[#4F46E5]" />}
                </button>;
              })}
            </div>
          </section>)}
          <div className="mt-7 flex items-center justify-between gap-3 border-t border-gray-100 pt-5">
            <button type="button" onClick={goPreviousGroup} disabled={groupStart === 0 || isSubmitting} className="flex items-center gap-1 rounded-xl border border-gray-200 px-4 py-3 font-bold text-gray-600 disabled:opacity-40"><ChevronLeft className="h-5 w-5" /> {isPassageGroup ? '上一组' : '上一题'}</button>
            <button type="button" onClick={() => setShowAnswerCard(true)} className="flex items-center gap-2 rounded-xl border border-indigo-200 px-4 py-3 font-bold text-[#4F46E5] lg:hidden"><Grid className="h-4 w-4" /> 答题卡</button>
            <button type="button" onClick={goNextGroup} disabled={isSubmitting} className="flex items-center gap-1 rounded-xl bg-[#4F46E5] px-5 py-3 font-bold text-white disabled:opacity-50">{groupEnd === scopedTest.questions.length - 1 ? '检查并交卷' : isPassageGroup ? '下一组' : '下一题'} <ChevronRight className="h-5 w-5" /></button>
          </div>
          <div className={`mt-4 text-center text-xs ${saveStatus.includes('失败') ? 'text-red-500' : 'text-gray-400'}`}>{saveStatus}</div>
        </main>
        <aside className="hidden h-fit rounded-3xl border border-indigo-50 bg-white p-5 shadow-sm lg:block lg:sticky lg:top-48">{answerCard}</aside>
      </div>

      {showAnswerCard && <div className="fixed inset-0 z-50 flex items-end bg-slate-950/40 p-3 lg:hidden" onClick={() => setShowAnswerCard(false)}><div className="max-h-[75vh] w-full overflow-auto rounded-3xl bg-white p-5" onClick={event => event.stopPropagation()}>{answerCard}</div></div>}

      {showSubmitDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-600"><AlertTriangle className="h-6 w-6" /></div>
            <h2 className="mt-4 text-2xl font-extrabold text-[#312E81]">确认提交{isPracticeMode ? '练习' : '试卷'}？</h2>
            <p className="mt-2 text-sm leading-6 text-gray-500">已答 {scopedTest.questions.length - unansweredCount} 题，未答 <strong className="text-amber-700">{unansweredCount}</strong> 题。提交后将立即显示正确答案和解析，且不能继续修改。</p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <button type="button" onClick={() => setShowSubmitDialog(false)} className="rounded-xl border border-gray-200 px-4 py-3 font-bold text-gray-600 hover:bg-gray-50">继续检查</button>
              <button type="button" onClick={() => void submitExam(false)} disabled={isSubmitting} className="rounded-xl bg-green-600 px-4 py-3 font-bold text-white hover:bg-green-700 disabled:opacity-50">{isSubmitting ? '提交中…' : '确认提交'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CSCAExam;
